import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isNil } from 'lodash';
import { parseEmojis } from '@src/util/parseEmojis';
import { replaceHtmlEntities } from '@src/util/replaceHtmlEntities';
import './styles.scss';

const TYPES = {
  NUMBER: 'number',
  PASSWORD: 'password',
  TEXT: 'text',
};

export const TextField = ({
  defaultValue,
  inputRef,
  type,
  messageType,
  fullWidth,
  multiline,
  clearable,
  min,
  max,
  message,
  placeholder,
  label,
  inputClassName,
  wrapperClassName,
  containerClassName,
  additionalElementClassName,
  onKeyDown,
  onChange,
  onBlur,
  additionalElements,
  onClear,
  onFocus,
  isDisabled,
}) => {
  // toggle element focus
  const [focused, setFocused] = useState(false);

  const setPlaceholder = (((focused && label) || !label) && placeholder) || '';

  const handleChange = (value: string): void => {
    const activeValue = parseEmojis(value);
    onChange && onChange(activeValue);
  };

  const handleBlur = e => {
    setFocused(false);
    onBlur && onBlur(e);
  };

  const handleKeyDown = e => {
    onKeyDown(e);

    // if user clicks 'e' key when type is 'number'
    if (type === TYPES.NUMBER && e.keyCode === 69) {
      e.preventDefault();
    }
  };

  const sharedProps = {
    className: classNames('fr-input__field', inputClassName, multiline && 'fr-input__field--multiline'),
    disabled: isDisabled,
    onBlur: handleBlur,
    onChange: e => handleChange(e.target.value),
    onFocus: () => {
      setFocused(true);

      if (onFocus) {
        onFocus();
      }
    },
    onKeyDown: handleKeyDown,
    onMouseDown: e => e.stopPropagation(),
    placeholder: setPlaceholder,
    ref: inputRef,
    value: !isNil(defaultValue) ? replaceHtmlEntities(defaultValue, true) : '',
  };

  const textAreaProps = {
    maxLength: max,
    minLength: min,
    ...sharedProps,
  };

  const inputProps = {
    max,
    min,
    type,
    ...sharedProps,
  };

  return (
    <div
      className={classNames('fr-input', wrapperClassName, {
        'fr-input--disabled': isDisabled,
        'fr-input--full-width': fullWidth,
      })}
    >
      <div
        className={classNames(
          'fr-input__container',
          messageType && `fr-input__container--${messageType}`,
          { 'fr-input__container--active': focused },
          { 'fr-input__container--multiline': multiline },
          containerClassName,
        )}
      >
        {multiline ? <textarea {...textAreaProps} /> : <input {...inputProps} />}

        {label && (
          <label
            className={classNames(
              'fr-input__label',
              messageType && `fr-input__label--${messageType}`,
              { 'fr-input__label--top': focused || defaultValue },
              { 'fr-input__label--active': focused },
            )}
          >
            {label}
          </label>
        )}

        {(additionalElements || clearable) && (
          <div
            className={classNames(
              (additionalElements || clearable) && 'fr-input__additional-elements',
              additionalElementClassName,
            )}
          >
            {additionalElements}

            {clearable && !isNil(defaultValue) && (
              <span
                className="fr-input__clear"
                onClick={() => {
                  handleChange('');
                  onClear();
                }}
              >
                &#10005;
              </span>
            )}
          </div>
        )}
      </div>

      {message && (
        <div className={classNames('fr-input__message', messageType && `fr-input__message--${messageType}`)}>
          {message}
        </div>
      )}
    </div>
  );
};

TextField.propTypes = {
  // Add className to additional element
  additionalElementClassName: PropTypes.string,

  // Display additional elements
  additionalElements: PropTypes.node,

  // Display "X" icon end of the input field.
  clearable: PropTypes.bool,

  // Add className to container div element
  containerClassName: PropTypes.string,

  // Initial input value
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  // In case fullWidth = true, component width will be 100%
  fullWidth: PropTypes.bool,

  // Add className to (input, textarea)
  inputClassName: PropTypes.string,

  // To access (input, textarea) dom element
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),

  isDisabled: PropTypes.bool,

  // Label for the input
  label: PropTypes.string,

  // Maximum value of input type=number or textarea characters length
  max: PropTypes.number,

  // Display message under the (input, textarea) field
  message: PropTypes.string,

  // To Change input field and message colors depend on messageType
  messageType: PropTypes.oneOf(['helper', 'error', false]),

  // Minimum value of input type=number or textarea characters length
  min: PropTypes.number,

  // Render textArea
  multiline: PropTypes.bool,

  onBlur: PropTypes.func,

  onChange: PropTypes.func,

  onClear: PropTypes.func,

  onFocus: PropTypes.func,

  /** Callback functions */
  onKeyDown: PropTypes.func,

  // Hint text
  placeholder: PropTypes.string,

  // HTML Input type
  type: PropTypes.oneOf(Object.values(TYPES)),

  // Add className to wrapper div element
  wrapperClassName: PropTypes.string,
};

TextField.defaultProps = {
  autoFocus: false,
  messageType: 'helper',
  onChange: () => null,
  onClear: () => null,
  onKeyDown: () => null,
  type: 'text',
};
